import { Box, Stack, theme } from '@dtx-company/design-system/src'
import { FC, useEffect, useRef, useState } from 'react'
import { OnProgressProps } from 'react-player/base'
import { SkeletonTheme } from 'react-loading-skeleton'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import useTranslation from 'next-translate/useTranslation'
const ReactPlayer = dynamic(() => import('react-player'), { ssr: false })

export const Video: FC<{ videoLink: string; placeholder?: string; heapId?: string }> = ({
  videoLink,
  placeholder,
  heapId = 'Home'
}) => {
  const [isVideoReady, setIsVideoReady] = useState(false)
  const progressRef = useRef<OnProgressProps>()
  const { t } = useTranslation('home')

  useEffect(() => {
    const handleRouteChange = (): void => {
      fireAnalyticsEvent(`${heapId}_Video_LeavingPage`, {
        ...progressRef.current
      })
    }

    window.addEventListener('beforeunload', handleRouteChange)

    return () => {
      window.removeEventListener('beforeunload', handleRouteChange)
    }
  }, [heapId])

  return (
    <Stack
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          marginX: 'auto',
          aspectRatio: '16 / 9',
          width: '100%',
          maxWidth: 844
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
          }}
        >
          {!isVideoReady && (
            <SkeletonTheme
              baseColor={theme.palette.grey[800]}
              highlightColor={theme.palette.grey[700]}
            >
              <Image
                src={placeholder || '/images/pages/home/learn-from-expert-placeholder.png'}
                alt={t('videoPlaceholderAlt')}
                quality={75}
                sizes="(max-width: 768px) 100vw, 75vw"
                fill
                style={{
                  objectFit: 'cover',
                  objectPosition: 'center center'
                }}
              />
            </SkeletonTheme>
          )}
          <ReactPlayer
            url={videoLink}
            width="100%"
            height="100%"
            config={{
              youtube: {
                playerVars: {
                  controls: true,
                  playsInline: true
                }
              },
              vimeo: {
                playerOptions: {
                  controls: true,
                  playsInline: true,
                  responsive: true,
                  pip: true
                }
              }
            }}
            onReady={() => setIsVideoReady(true)}
            onPlay={() => {
              fireAnalyticsEvent(`${heapId}_Video_Played`)
            }}
            onPause={() => {
              fireAnalyticsEvent(`${heapId}_Video_Paused`, {
                ...progressRef.current
              })
            }}
            onProgress={props => {
              progressRef.current = props
            }}
            style={{
              display: isVideoReady ? 'block' : 'hidden',
              background: 'transparent',
              overflow: 'hidden'
            }}
          />
        </Box>
      </Box>
    </Stack>
  )
}
